import React, { useState } from "react";
import Modal from "../../modal-new";
import submit from "submit-form";
import { SearchDropdown } from "../../inputs/search-dropdown";
import DateInput from "../../inputs/date-input";
import moment from "moment";
import OptionToggler from "../../OptionToggler";
import convert from "convert-units";
import { round } from "lodash";
import SvgIcon from "../../SvgIcon";
import { goalValidation } from "../../../utils/goals-validations";
import PropTypes from "prop-types";
import SleepInput from "../../inputs/SleepInput";

const unitsOptions = [
  { label: "Kg", value: "metric" },
  { label: "Lbs", value: "imperial" },
];

const customGoalsIcons = [
  {
    value: "swimming",
  },
  {
    value: "arrow",
  },
  {
    value: "stretching",
  },
  {
    value: "baseball",
  },
  {
    value: "meditate",
  },
  {
    value: "bicycle",
  },
];

const buildMovementOptionsArray = (activeUserToRequest) => {
  const {
    squat,
    bench,
    deadlift,
    assistant,
    clean = [],
    jerk = [],
    snatch = [],
    overheadPress = [],
    other = [],
  } = activeUserToRequest?.exerciseLibrary || {};

  const movementOptions = [
    {
      label: "Squat",
      primary: "squat",
      type: "primary",
    },
    {
      label: "Bench",
      primary: "bench",
      type: "primary",
    },
    {
      label: "Deadlift",
      primary: "deadlift",
      type: "primary",
    },
    {
      label: "Clean",
      primary: "clean",
      type: "primary",
    },
    {
      label: "Jerk",
      primary: "jerk",
      type: "primary",
    },
    {
      label: "Snatch",
      primary: "snatch",
      type: "primary",
    },
    {
      label: "Overhead Press",
      primary: "overheadPress",
      type: "primary",
    },
  ];

  squat.forEach((item) => {
    movementOptions.push({
      label: item.movement + " " + "(Squat)",
      primary: "squat",
      type: "secondary",
      secondary: item.movement,
    });
  });
  bench.forEach((item) => {
    movementOptions.push({
      label: item.movement + " " + "(Bench)",
      primary: "bench",
      type: "secondary",
      secondary: item.movement,
    });
  });
  deadlift.forEach((item) => {
    movementOptions.push({
      label: item.movement + " " + "(Deadlift)",
      primary: "deadlift",
      type: "secondary",
      secondary: item.movement,
    });
  });
  clean.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "clean",
      type: "secondary",
      secondary: item.movement,
    });
  });
  jerk.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "jerk",
      type: "secondary",
      secondary: item.movement,
    });
  });
  snatch.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "snatch",
      type: "secondary",
      secondary: item.movement,
    });
  });
  overheadPress.forEach((item) => {
    movementOptions.push({
      label: item.movement + " " + "(Overhead Press)",
      primary: "overheadPress",
      type: "secondary",
      secondary: item.movement,
    });
  });
  other.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "other",
      type: "secondary",
      secondary: item.movement,
    });
  });
  assistant.forEach((item) => {
    movementOptions.push({
      label: item.movement,
      primary: "assistant",
      type: "assistant",
      secondary: item.movement,
    });
  });

  return movementOptions;
};

const AddGoal = ({ locals }) => {
  const { units, activeUserToRequest, query, assert, flash, clientGoals } =
    locals;
  const { modalState, goalId } = query;

  const currentGoal = clientGoals.find((goal) => goal._id === goalId);

  const movementOptions = buildMovementOptionsArray(activeUserToRequest);

  const [selectedExercise, setSelectedExercise] = useState(
    currentGoal ? currentGoal?.details.targetName : ""
  );
  const [targetExercise, setTargetExercise] = useState(null);
  const [targetSteps, setTargetSteps] = useState(
    currentGoal ? currentGoal?.details.targetSteps : ""
  );
  const [targetSleep, setTargetSleep] = useState(
    currentGoal ? currentGoal?.details.targetSleep : ""
  );
  const [targetReps, setTargetReps] = useState(
    currentGoal ? currentGoal?.details.targetReps : ""
  );
  const [targetLoad, setTargetLoad] = useState(
    currentGoal
      ? units === "metric"
        ? round(currentGoal?.details.targetLoad || 0, 2)
        : round(
            convert(currentGoal?.details.targetLoad || 0)
              .from("kg")
              .to("lb"),
            2
          )
      : ""
  );
  const [targetWeight, setTargetWeight] = useState(
    currentGoal
      ? units === "metric"
        ? round(currentGoal?.details.targetWeight || 0, 2)
        : round(
            convert(currentGoal?.details.targetWeight || 0)
              .from("kg")
              .to("lb"),
            2
          )
      : ""
  );
  const [targetDate, setTargetDate] = useState(
    currentGoal
      ? moment(currentGoal?.targetDate).toDate()
      : moment().add(1, "month").toDate()
  );
  const [targetUnits, setTargetUnits] = useState(units);
  const [customName, setCustomName] = useState(
    currentGoal ? currentGoal?.details.customName : ""
  );
  const [customTarget, setCustomTarget] = useState(
    currentGoal ? currentGoal?.details.customTarget : ""
  );
  const [customIcon, setCustomIcon] = useState(
    currentGoal ? currentGoal?.details.customIcon : ""
  );

  const submitGoal = () => {
    const startDate = moment().format("YYYY-MM-DD");
    const utcStartDate = Number(moment().format("YYYYMMDD"));

    const details = {};

    if (modalState === "personalRecord") {
      const convertedLoad =
        targetUnits === "metric"
          ? targetLoad
          : round(convert(targetLoad).from("lb").to("kg"), 5);

      const { primary, secondary, type } = targetExercise || {};

      details.targetName = selectedExercise;
      details.targetPrimary = primary;
      details.targetSecondary = secondary;
      details.targetType = type;
      details.targetReps = Number(targetReps);
      details.targetLoad = Number(convertedLoad);
    }

    if (modalState === "custom") {
      details.customName = customName;
      details.customTarget = customTarget;
      details.customIcon = customIcon;
    }

    if (modalState === "steps") {
      details.targetSteps = targetSteps;
    }

    if (modalState === "sleep") {
      details.targetSleep = targetSleep;
    }

    if (modalState === "bodyWeight") {
      const convertedWeight =
        targetUnits === "metric"
          ? targetWeight
          : round(convert(targetWeight).from("lb").to("kg"), 5);

      details.targetWeight = convertedWeight;
    }

    const isValidated = goalValidation(modalState, details, assert, flash);

    if (isValidated) {
      const data = {
        startDate,
        utcStartDate,
        type: modalState,
        details,
      };

      if (modalState === "personalRecord" || modalState === "bodyWeight") {
        data.utcTargetDate = Number(moment(targetDate).format("YYYYMMDD"));
        data.targetDate = moment(targetDate).format("YYYY-MM-DD");
      }

      const url = currentGoal
        ? `/actions/client-goals/edit-goal/${currentGoal._id}?proxyAthlete=${query.athlete}`
        : `/actions/client-goals/add-new-goal?proxyAthlete=${query.athlete}`;

      submit(url, {
        method: "POST",
        body: {
          ...data,
        },
      });
    }
  };

  return (
    <Modal
      className={"add-goal-modal"}
      title="Personal record goal"
      toClear={["modalState", "goalId"]}
    >
      <div className="body">
        {modalState === "personalRecord" && (
          <>
            <div className="form-col">
              <label htmlFor="selectExercise">
                Select an existing exercise
              </label>
              <SearchDropdown
                options={movementOptions}
                placeholder="Select Exercise"
                handleChange={(val) => {
                  if (val) {
                    const { label } = val;

                    setSelectedExercise(label);
                    setTargetExercise(val);
                  }
                }}
                value={selectedExercise}
                valueKey="label"
              />
            </div>

            <div className="form-col">
              <label htmlFor="targetReps">Number of reps</label>

              <input
                type="number"
                name="targetReps"
                placeholder="Enter reps"
                className="question-input"
                value={targetReps}
                onChange={(e) => {
                  const { value } = e.target;

                  setTargetReps(String(value));
                }}
              />
            </div>

            <div className="form-col">
              <label htmlFor="targetReps">Load</label>
              <div className="form-row">
                <input
                  type="number"
                  name="targetLoad"
                  placeholder="Enter load"
                  className="question-input"
                  value={targetLoad}
                  onChange={(e) => {
                    const { value } = e.target;

                    setTargetLoad(String(value));
                  }}
                />
                <div className="metric-toggler">
                  <OptionToggler
                    name={"units"}
                    options={unitsOptions}
                    defaultValue={units}
                    onSelectFn={(value) => {
                      setTargetUnits(value);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="form-col">
              <label htmlFor="targetDate">Target Date</label>

              <DateInput
                name="targetDate"
                placeholder="Target Date"
                value={targetDate}
                min={moment().toDate()}
                onChange={(date) => setTargetDate(date)}
              />
            </div>
          </>
        )}

        {modalState === "custom" && (
          <>
            <div className="form-col">
              <label htmlFor="customName">Goal Name</label>

              <input
                type="text"
                name="customName"
                placeholder="Enter name"
                className="question-input"
                value={customName}
                onChange={(e) => {
                  const { value } = e.target;

                  setCustomName(String(value));
                }}
              />
            </div>
            <div className="form-col">
              <label htmlFor="customTarget">Goal Target</label>

              <input
                type="text"
                name="customTarget"
                placeholder="Enter target"
                className="question-input"
                value={customTarget}
                onChange={(e) => {
                  const { value } = e.target;

                  setCustomTarget(String(value));
                }}
              />
            </div>

            <div>
              <label htmlFor="selectIcon">Select Icon</label>

              <div className="goal-icons">
                {customGoalsIcons.map((icon) => (
                  <button
                    key={`${icon.value}-icon`}
                    data-button-type={
                      icon.value === customIcon ? "primary" : "secondary"
                    }
                    data-icon={true}
                    onClick={() => {
                      setCustomIcon(icon.value);
                    }}
                  >
                    <SvgIcon src={`/img/new-icons/${icon.value}.svg`} />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {modalState === "steps" && (
          <div className="form-col">
            <label htmlFor="targetSteps">Daily step count goal</label>

            <input
              type="number"
              name="targetSteps"
              placeholder="Enter steps"
              className="question-input"
              value={targetSteps}
              onChange={(e) => {
                const { value } = e.target;

                setTargetSteps(String(value));
              }}
            />
          </div>
        )}

        {modalState === "sleep" && (
          <div className="form-col">
            <label htmlFor="targetSleep">Daily sleep count goal</label>

            <SleepInput
              defaultValue={targetSleep}
              isSleepXD={true}
              name={"sleep-goal"}
              setSleepTime={setTargetSleep}
            />
          </div>
        )}

        {modalState === "bodyWeight" && (
          <>
            <div className="form-col">
              <label htmlFor="targetReps">Bodyweight</label>
              <div className="form-row">
                <input
                  type="number"
                  name="targetWeight"
                  placeholder="Enter bodyweight"
                  className="question-input"
                  value={targetWeight}
                  onChange={(e) => {
                    const { value } = e.target;

                    setTargetWeight(String(value));
                  }}
                />
                <div className="metric-toggler">
                  <OptionToggler
                    name={"units"}
                    options={unitsOptions}
                    defaultValue={units}
                    onSelectFn={(value) => {
                      setTargetUnits(value);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="form-col">
              <label htmlFor="targetDate">Target Date</label>

              <DateInput
                name="targetDate"
                placeholder="Target Date"
                value={targetDate}
                min={moment().toDate()}
                onChange={(date) => setTargetDate(date)}
              />
            </div>
          </>
        )}

        <div className="flex justify-end">
          <button
            onClick={(e) => {
              e.preventDefault();
              submitGoal();
            }}
            data-button-type="primary"
          >
            Set
          </button>
        </div>
      </div>
    </Modal>
  );
};

AddGoal.propTypes = {
  locals: PropTypes.any,
};

export default AddGoal;
